<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type NewsTickerItem } from '../api/client'

const items = ref<NewsTickerItem[]>([])

let pollTimer: ReturnType<typeof setInterval> | null = null

async function fetchItems() {
  try { items.value = await api.newsTicker(20) }
  catch { /* keep existing items */ }
}

function startPolling() {
  if (pollTimer !== null) return
  pollTimer = setInterval(fetchItems, 60_000)
}
function stopPolling() {
  if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null }
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') stopPolling()
  else { startPolling(); fetchItems() }
}

function labelClass(label: string | null): string {
  if (!label) return 'neutral'
  if (label.includes('Bullish')) return 'bull'
  if (label.includes('Bearish')) return 'bear'
  return 'neutral'
}

onMounted(async () => {
  document.addEventListener('visibilitychange', onVisibilityChange)
  await fetchItems()
  startPolling()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  stopPolling()
})

const marqueeItems = computed(() => items.value)
// Marquee speed: tuned to ~15s per headline in the reading zone (user
// feedback 2026-04-20). Same behaviour on desktop and mobile — mobile
// just renders a smaller viewport of the same scrolling track.
const loopDurationSec = computed(() => Math.max(120, items.value.length * 16))
</script>

<template>
  <div v-if="items.length > 0" class="news-ticker-bar">
    <div class="marquee-wrap">
      <div class="marquee-track" :style="{ animationDuration: `${loopDurationSec}s` }">
        <a
          v-for="(item, i) in marqueeItems"
          :key="`a-${i}`"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          class="marquee-item"
        >
          <span class="ticker" :class="labelClass(item.sentimentLabel)">{{ item.ticker }}</span>
          <span class="headline">: {{ item.headline }}</span>
          <span class="sep">•</span>
        </a>
        <a
          v-for="(item, i) in marqueeItems"
          :key="`b-${i}`"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          class="marquee-item"
        >
          <span class="ticker" :class="labelClass(item.sentimentLabel)">{{ item.ticker }}</span>
          <span class="headline">: {{ item.headline }}</span>
          <span class="sep">•</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.news-ticker-bar {
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
  height: 26px;
  display: flex;
  align-items: center;
}

.marquee-wrap { width: 100%; overflow: hidden; white-space: nowrap; }
.marquee-track {
  display: inline-block;
  animation: scroll-left linear infinite;
  animation-play-state: running;
}
.marquee-wrap:hover .marquee-track { animation-play-state: paused; }

.marquee-item {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: #c9d1d9;
  font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
  padding: 0 6px;
}
.marquee-item .headline { color: #c9d1d9; }
.marquee-item .sep { color: #4a5560; margin: 0 8px; }
.ticker { font-weight: 600; }
.ticker.bull    { color: #4caf50; }
.ticker.bear    { color: #f44336; }
.ticker.neutral { color: #c9d1d9; }

@keyframes scroll-left {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

@media (max-width: 768px) {
  .news-ticker-bar { height: 22px; }
  .marquee-item { font-size: 11px; }
}
</style>
